/**
 * @typedef {'active' | 'due-service' | 'repair' | 'inactive'} VehicleStatus
 * @typedef {'completed' | 'pending' | 'in-progress'} MaintenanceStatus
 * @typedef {'Tyres' | 'Service' | 'Battery' | 'Repairs' | 'Safety Equipment' | 'Accessories'} CostCategory
 */

/** @type {readonly string[]} */
export const MAINTENANCE_ITEMS = [
  'Plate Number Fitting',
  'Lights & Mirror Marking',
  'Vehicle Deep Clean',
  'Window Tint',
  'Fire Extinguisher',
  'First Aid Kit',
  '5,000 KM Service',
  'Tyres & Wheel Alignment',
  'Mud Flaps',
  'Battery Replacement',
  'Rear Light Repair',
  'Side Mirror Repair',
  'Front Bumper Repair',
  'GPS Installation',
  'Triangle'
];

export const SERVICE_INTERVAL_KM = 5000;

/** @type {Array<{ id: string; plate: string; model: string; driver: string; project: string; odometer: number; status: VehicleStatus; lastServiceDate: string; lastServiceOdometer: number; insuranceExpiry: string; roadLicenceExpiry: string; phone: string; email: string; ownerRentalCompany: string; driverAvatar: string }>} */
export const FLEET_VEHICLES = [
  {
    id: 'v1',
    plate: 'T105ESF',
    model: 'Toyota Hilux',
    driver: 'Geoffrey',
    project: 'LOT 8',
    odometer: 151643,
    status: 'active',
    lastServiceDate: '2025-11-18',
    lastServiceOdometer: 147200,
    insuranceExpiry: '2026-08-15',
    roadLicenceExpiry: '2026-05-30',
    phone: '+255 712 345 001',
    email: 'geoffrey.mwangi@sagemcom.com',
    ownerRentalCompany: 'Simba Fleet Services',
    driverAvatar: '/assets/driver-avatars/default.svg'
  },
  {
    id: 'v2',
    plate: 'T112ESF',
    model: 'Toyota Hilux',
    driver: 'MWAKIBETE Philipo',
    project: 'LOT 10',
    odometer: 158132,
    status: 'active',
    lastServiceDate: '2025-09-02',
    lastServiceOdometer: 153000,
    insuranceExpiry: '2026-03-20',
    roadLicenceExpiry: '2026-04-12',
    phone: '+255 713 456 002',
    email: 'philipo.mwakibete@sagemcom.com',
    ownerRentalCompany: 'Alpha Car Hire Tanzania',
    driverAvatar: '/assets/driver-avatars/default.svg'
  },
  {
    id: 'v3',
    plate: 'T993ESE',
    model: 'Toyota Hilux',
    driver: 'KIPRUTO Shadrack',
    project: 'LOT 10',
    odometer: 149054,
    status: 'active',
    lastServiceDate: '2025-12-10',
    lastServiceOdometer: 144800,
    insuranceExpiry: '2026-11-01',
    roadLicenceExpiry: '2026-07-22',
    phone: '+255 714 567 003',
    email: 'shadrack.kipruto@sagemcom.com',
    ownerRentalCompany: 'Premier Vehicle Rentals',
    driverAvatar: '/assets/driver-avatars/default.svg'
  },
  {
    id: 'v4',
    plate: 'T104ESF',
    model: 'Toyota Fortuner',
    driver: 'Admin',
    project: 'Admin',
    odometer: 139965,
    status: 'active',
    lastServiceDate: '2026-01-05',
    lastServiceOdometer: 135500,
    insuranceExpiry: '2026-02-28',
    roadLicenceExpiry: '2026-06-18',
    phone: '+255 715 678 004',
    email: 'admin@sagemcom.local',
    ownerRentalCompany: 'Sagemcom Logistics (Owned)',
    driverAvatar: '/assets/driver-avatars/default.svg'
  }
];

/** @type {Array<{ vehicleId: string; licenseNumber: string; fullName: string; licenseClass: string; issueDate: string; expiryDate: string; issuingAuthority: string; nationalId: string; frontImage: string }>} */
export const DRIVING_LICENSES = [
  {
    vehicleId: 'v1',
    licenseNumber: 'DL-2019-45821',
    fullName: 'Geoffrey Mwangi',
    licenseClass: 'C, D',
    issueDate: '2019-03-15',
    expiryDate: '2026-03-14',
    issuingAuthority: 'Tanzania Revenue Authority (TRA)',
    nationalId: '19850312-12345-00001-12',
    frontImage: '/assets/driving-licenses/dl-front.svg'
  },
  {
    vehicleId: 'v2',
    licenseNumber: 'DL-2020-88234',
    fullName: 'Philipo Mwakibete',
    licenseClass: 'C, D',
    issueDate: '2020-06-22',
    expiryDate: '2026-06-21',
    issuingAuthority: 'Tanzania Revenue Authority (TRA)',
    nationalId: '19910708-23456-00002-23',
    frontImage: '/assets/driving-licenses/dl-front.svg'
  },
  {
    vehicleId: 'v3',
    licenseNumber: 'DL-2018-33109',
    fullName: 'KIPRUTO Shadrack',
    licenseClass: 'C',
    issueDate: '2018-11-05',
    expiryDate: '2025-11-04',
    issuingAuthority: 'Tanzania Revenue Authority (TRA)',
    nationalId: '19881220-34567-00003-34',
    frontImage: '/assets/driving-licenses/dl-front.svg'
  },
  {
    vehicleId: 'v4',
    licenseNumber: 'DL-2021-99001',
    fullName: 'Admin User',
    licenseClass: 'B, C, D',
    issueDate: '2021-01-10',
    expiryDate: '2027-01-09',
    issuingAuthority: 'Tanzania Revenue Authority (TRA)',
    nationalId: '19800101-45678-00004-45',
    frontImage: '/assets/driving-licenses/dl-front.svg'
  }
];

/** @type {Array<{ id: string; vehicleId: string; date: string; item: string; category: CostCategory; supplier: string; cost: number; invoiceNumber: string; attachment: string | null; status: MaintenanceStatus; servicedOdometer?: number }>} */
export const MAINTENANCE_RECORDS = [
  { id: 'm1', vehicleId: 'v1', date: '2025-06-12', item: '5,000 KM Service', category: 'Service', supplier: 'Toyota Tanzania', cost: 485000, invoiceNumber: 'TT-2025-0612', attachment: 'service-report.pdf', status: 'completed', servicedOdometer: 137200 },
  { id: 'm2', vehicleId: 'v1', date: '2025-07-08', item: 'Tyres & Wheel Alignment', category: 'Tyres', supplier: 'Kilele Auto Parts', cost: 1250000, invoiceNumber: 'KAP-77821', attachment: 'tyres-invoice.pdf', status: 'completed' },
  { id: 'm3', vehicleId: 'v1', date: '2025-08-14', item: 'Fire Extinguisher', category: 'Safety Equipment', supplier: 'Safety First TZ', cost: 85000, invoiceNumber: 'SF-4412', attachment: null, status: 'completed' },
  { id: 'm4', vehicleId: 'v1', date: '2025-09-20', item: 'Battery Replacement', category: 'Battery', supplier: 'Swift Auto', cost: 320000, invoiceNumber: 'SA-99201', attachment: 'battery.pdf', status: 'completed' },
  { id: 'm5', vehicleId: 'v1', date: '2025-11-18', item: '5,000 KM Service', category: 'Service', supplier: 'Toyota Tanzania', cost: 512000, invoiceNumber: 'TT-2025-1118', attachment: 'service-nov.pdf', status: 'completed', servicedOdometer: 147200 },
  { id: 'm6', vehicleId: 'v2', date: '2025-05-22', item: 'GPS Installation', category: 'Accessories', supplier: 'TrackPro Solutions', cost: 280000, invoiceNumber: 'TP-5521', attachment: 'gps.pdf', status: 'completed' },
  { id: 'm7', vehicleId: 'v2', date: '2025-07-15', item: 'Front Bumper Repair', category: 'Repairs', supplier: 'Metro Body Works', cost: 890000, invoiceNumber: 'MBW-3310', attachment: null, status: 'completed' },
  { id: 'm8', vehicleId: 'v2', date: '2025-09-02', item: '5,000 KM Service', category: 'Service', supplier: 'Toyota Tanzania', cost: 498000, invoiceNumber: 'TT-2025-0902', attachment: 'service-sep.pdf', status: 'completed', servicedOdometer: 153000 },
  { id: 'm9', vehicleId: 'v2', date: '2025-10-30', item: 'Mud Flaps', category: 'Accessories', supplier: 'Kilele Auto Parts', cost: 120000, invoiceNumber: 'KAP-88102', attachment: null, status: 'completed' },
  { id: 'm10', vehicleId: 'v2', date: '2026-01-14', item: 'Side Mirror Repair', category: 'Repairs', supplier: 'Metro Body Works', cost: 185000, invoiceNumber: 'MBW-4102', attachment: null, status: 'pending' },
  { id: 'm11', vehicleId: 'v3', date: '2025-06-05', item: 'Window Tint', category: 'Accessories', supplier: 'Elite Tint TZ', cost: 220000, invoiceNumber: 'ET-2205', attachment: 'tint.pdf', status: 'completed' },
  { id: 'm12', vehicleId: 'v3', date: '2025-08-28', item: 'Tyres & Wheel Alignment', category: 'Tyres', supplier: 'Kilele Auto Parts', cost: 1180000, invoiceNumber: 'KAP-80288', attachment: 'tyres-aug.pdf', status: 'completed' },
  { id: 'm13', vehicleId: 'v3', date: '2025-10-12', item: 'Rear Light Repair', category: 'Repairs', supplier: 'Swift Auto', cost: 145000, invoiceNumber: 'SA-10122', attachment: null, status: 'in-progress' },
  { id: 'm14', vehicleId: 'v3', date: '2025-12-10', item: '5,000 KM Service', category: 'Service', supplier: 'Toyota Tanzania', cost: 505000, invoiceNumber: 'TT-2025-1210', attachment: 'service-dec.pdf', status: 'completed', servicedOdometer: 144800 },
  { id: 'm15', vehicleId: 'v3', date: '2026-01-08', item: 'First Aid Kit', category: 'Safety Equipment', supplier: 'Safety First TZ', cost: 65000, invoiceNumber: 'SF-5108', attachment: null, status: 'completed' },
  { id: 'm16', vehicleId: 'v4', date: '2025-05-10', item: 'Vehicle Deep Clean', category: 'Accessories', supplier: 'Sparkle Fleet Care', cost: 95000, invoiceNumber: 'SFC-0510', attachment: null, status: 'completed' },
  { id: 'm17', vehicleId: 'v4', date: '2025-07-20', item: 'Triangle', category: 'Safety Equipment', supplier: 'Safety First TZ', cost: 35000, invoiceNumber: 'SF-3720', attachment: null, status: 'completed' },
  { id: 'm18', vehicleId: 'v4', date: '2025-09-15', item: 'Lights & Mirror Marking', category: 'Accessories', supplier: 'Swift Auto', cost: 78000, invoiceNumber: 'SA-91515', attachment: null, status: 'completed' },
  { id: 'm19', vehicleId: 'v4', date: '2025-11-02', item: 'Plate Number Fitting', category: 'Accessories', supplier: 'TRA Services', cost: 45000, invoiceNumber: 'TRA-1102', attachment: null, status: 'completed' },
  { id: 'm20', vehicleId: 'v4', date: '2026-01-05', item: '5,000 KM Service', category: 'Service', supplier: 'Toyota Tanzania', cost: 620000, invoiceNumber: 'TT-2026-0105', attachment: 'fortuner-service.pdf', status: 'completed', servicedOdometer: 135500 },
  { id: 'm21', vehicleId: 'v1', date: '2026-02-20', item: 'Mud Flaps', category: 'Accessories', supplier: 'Kilele Auto Parts', cost: 115000, invoiceNumber: 'KAP-22026', attachment: null, status: 'completed' },
  { id: 'm22', vehicleId: 'v2', date: '2026-02-05', item: 'First Aid Kit', category: 'Safety Equipment', supplier: 'Safety First TZ', cost: 68000, invoiceNumber: 'SF-2205', attachment: null, status: 'completed' }
];

/** @type {Array<{ month: string; label: string; cost: number }>} */
export const MONTHLY_COST_TREND = [
  { month: '2025-09', label: 'Sep 2025', cost: 1683000 },
  { month: '2025-10', label: 'Oct 2025', cost: 1325000 },
  { month: '2025-11', label: 'Nov 2025', cost: 2102000 },
  { month: '2025-12', label: 'Dec 2025', cost: 570000 },
  { month: '2026-01', label: 'Jan 2026', cost: 1438000 },
  { month: '2026-02', label: 'Feb 2026', cost: 368000 }
];

export const CATEGORY_COLORS = {
  Tyres: '#00A651',
  Service: '#0284c7',
  Battery: '#7c3aed',
  Repairs: '#ea580c',
  'Safety Equipment': '#0891b2',
  Accessories: '#64748b'
};

export function getNextServiceDue(vehicle) {
  const nextKm = vehicle.lastServiceOdometer + SERVICE_INTERVAL_KM;
  const kmRemaining = nextKm - vehicle.odometer;
  return {
    nextKm,
    kmRemaining,
    isDue: kmRemaining <= 0,
    isSoon: kmRemaining > 0 && kmRemaining <= 800
  };
}

export function getLatestInspectionOdometer(virRows, fallbackOdometer) {
  const latest = virRows
    .filter((r) => r.overall !== 'inspect' && r.odometer != null)
    .sort((a, b) => b.date.localeCompare(a.date))[0];
  return latest?.odometer ?? fallbackOdometer;
}

export function getServiceCardIntervalStatus(nextServiceKm, inspectionOdometer) {
  if (inspectionOdometer == null) {
    return { label: 'Awaiting Inspection', status: 'pending' };
  }
  const kmRemaining = nextServiceKm - inspectionOdometer;
  if (kmRemaining <= 0) {
    return { label: 'Service Due', status: 'due-service' };
  }
  if (kmRemaining <= 800) {
    return { label: 'Due Soon', status: 'pending' };
  }
  return { label: 'Card Valid', status: 'active' };
}

export function buildServiceCardRows(vehicleId, inspectionOdometer, records = MAINTENANCE_RECORDS) {
  const services = records
    .filter((r) => r.vehicleId === vehicleId && r.category === 'Service')
    .sort((a, b) => b.date.localeCompare(a.date));

  return services.map((record, index) => {
    const servicedMileage = record.servicedOdometer ?? 0;
    const nextServiceKm =
      record.nextServiceKm != null && Number.isFinite(Number(record.nextServiceKm))
        ? Number(record.nextServiceKm)
        : servicedMileage + SERVICE_INTERVAL_KM;
    const intervalStatus = index === 0
      ? getServiceCardIntervalStatus(nextServiceKm, inspectionOdometer)
      : { label: 'Serviced', status: 'completed' };

    return {
      id: record.id,
      date: record.date,
      servicedMileage,
      nextServiceKm,
      statusLabel: intervalStatus.label,
      statusClass: intervalStatus.status
    };
  });
}

export function getVehicleTotalCost(vehicleId, records = MAINTENANCE_RECORDS) {
  return records
    .filter((r) => r.vehicleId === vehicleId)
    .reduce((sum, r) => sum + r.cost, 0);
}

export function getVehicleCostPerKm(vehicle, totalCost) {
  if (!vehicle.odometer) return 0;
  return totalCost / vehicle.odometer;
}

export function enrichVehicles(records = MAINTENANCE_RECORDS) {
  return FLEET_VEHICLES.map((v) => {
    const totalCost = getVehicleTotalCost(v.id, records);
    const service = getNextServiceDue(v);
    let status = v.status;
    if (service.isDue && status === 'active') status = 'due-service';
    return {
      ...v,
      totalCost,
      costPerKm: getVehicleCostPerKm(v, totalCost),
      nextServiceDue: service,
      displayStatus: status
    };
  });
}

export function getCategoryBreakdown(records = MAINTENANCE_RECORDS) {
  const map = {};
  records.forEach((r) => {
    map[r.category] = (map[r.category] || 0) + r.cost;
  });
  return Object.entries(map).map(([name, value]) => ({ name, value }));
}

export function getNotifications(vehicles = enrichVehicles()) {
  const items = [];
  const today = new Date();

  vehicles.forEach((v) => {
    if (!v?.id) return;
    const costPerKm = Number(v.costPerKm) || 0;
    if (costPerKm > 0.012) {
      items.push({
        id: `cost-${v.id}`,
        type: 'warning',
        title: 'High maintenance cost',
        message: `${v.plate} (${v.driver}) — ${(costPerKm * 1000).toFixed(1)} TZS/km`,
        date: today.toISOString().slice(0, 10)
      });
    }
    const nextService = v.nextServiceDue;
    if (nextService && (nextService.isDue || nextService.isSoon)) {
      items.push({
        id: `service-${v.id}`,
        type: 'alert',
        title: nextService.isDue ? 'Service overdue' : 'Service due soon',
        message: `${v.model || 'Vehicle'} ${v.plate} — ${nextService.isDue ? 'Overdue' : `${nextService.kmRemaining} KM remaining`}`,
        date: today.toISOString().slice(0, 10)
      });
    }
    if (v.insuranceExpiry) {
      const ins = new Date(v.insuranceExpiry);
      if (!Number.isNaN(ins.getTime())) {
        const daysIns = Math.ceil((ins - today) / (1000 * 60 * 60 * 24));
        if (daysIns <= 60) {
          items.push({
            id: `ins-${v.id}`,
            type: daysIns <= 30 ? 'danger' : 'warning',
            title: 'Insurance expiry',
            message: `${v.plate} expires ${v.insuranceExpiry} (${daysIns} days)`,
            date: v.insuranceExpiry
          });
        }
      }
    }
    if (v.roadLicenceExpiry) {
      const lic = new Date(v.roadLicenceExpiry);
      if (!Number.isNaN(lic.getTime())) {
        const daysLic = Math.ceil((lic - today) / (1000 * 60 * 60 * 24));
        if (daysLic <= 60) {
          items.push({
            id: `lic-${v.id}`,
            type: daysLic <= 30 ? 'danger' : 'warning',
            title: 'Road licence expiry',
            message: `${v.plate} expires ${v.roadLicenceExpiry} (${daysLic} days)`,
            date: v.roadLicenceExpiry
          });
        }
      }
    }
  });

  return items.sort((a, b) => (a.type === 'danger' ? -1 : 1));
}

export function getUpcomingMaintenance(vehicles = enrichVehicles()) {
  return vehicles
    .map((v) => ({
      vehicleId: v.id,
      plate: v.plate,
      driver: v.driver,
      model: v.model,
      odometer: v.odometer,
      nextKm: v.nextServiceDue.nextKm,
      kmRemaining: v.nextServiceDue.kmRemaining,
      isDue: v.nextServiceDue.isDue,
      lastServiceDate: v.lastServiceDate
    }))
    .sort((a, b) => a.kmRemaining - b.kmRemaining);
}

/** @type {Array<{ id: string; vehicleId: string; date: string; liters: number; previousOdometer?: number; odometer: number; fuelCost: number; fuelCardNumber?: string; cardUsed: boolean; station: string }>} */
export const FUEL_RECORDS = [
  {
    id: 'f1',
    vehicleId: 'v1',
    date: '2026-02-28',
    liters: 62,
    previousOdometer: 150920,
    odometer: 151643,
    fuelCost: 186000,
    fuelCardNumber: 'TE-88421',
    cardUsed: true,
    station: 'TotalEnergies Mikocheni'
  },
  {
    id: 'f2',
    vehicleId: 'v1',
    date: '2026-02-14',
    liters: 58,
    previousOdometer: 150362,
    odometer: 150920,
    fuelCost: 174000,
    fuelCardNumber: 'TE-88421',
    cardUsed: true,
    station: 'TotalEnergies Ubungo'
  },
  {
    id: 'f3',
    vehicleId: 'v2',
    date: '2026-03-01',
    liters: 65,
    previousOdometer: 157410,
    odometer: 158132,
    fuelCost: 195000,
    fuelCardNumber: 'TE-55210',
    cardUsed: true,
    station: 'TotalEnergies Arusha'
  },
  {
    id: 'f4',
    vehicleId: 'v2',
    date: '2026-02-18',
    liters: 54,
    previousOdometer: 156856,
    odometer: 157410,
    fuelCost: 162000,
    fuelCardNumber: '',
    cardUsed: false,
    station: 'TotalEnergies Manyara'
  },
  {
    id: 'f5',
    vehicleId: 'v3',
    date: '2026-02-25',
    liters: 60,
    previousOdometer: 148494,
    odometer: 149054,
    fuelCost: 180000,
    fuelCardNumber: 'TE-33108',
    cardUsed: true,
    station: 'TotalEnergies Babati'
  },
  {
    id: 'f6',
    vehicleId: 'v4',
    date: '2026-02-20',
    liters: 72,
    previousOdometer: 139293,
    odometer: 139965,
    fuelCost: 216000,
    fuelCardNumber: 'TE-99001',
    cardUsed: true,
    station: 'TotalEnergies DSM'
  }
];

/** @type {Array<{ id: string; vehicleId: string; date: string; location: string; odometer: number; issue: string; status: 'reported' | 'under-repair' | 'resolved'; reportedBy: string; attachmentPhoto?: string | null; attachmentPhotoName?: string }>} */
export const BREAKDOWN_RECORDS = [
  {
    id: 'bd1',
    vehicleId: 'v1',
    date: '2025-11-22',
    location: 'Arusha–Moshi highway (Km 42)',
    odometer: 146200,
    issue: 'Engine overheating — coolant hose burst',
    status: 'resolved',
    reportedBy: 'Geoffrey',
    attachmentPhoto: null,
    attachmentPhotoName: ''
  },
  {
    id: 'bd2',
    vehicleId: 'v1',
    date: '2026-01-08',
    location: 'Site access road, BTS Dodoma',
    odometer: 149850,
    issue: 'Flat tyre — rear left puncture on rocky terrain',
    status: 'resolved',
    reportedBy: 'Geoffrey',
    attachmentPhoto: null,
    attachmentPhotoName: ''
  },
  {
    id: 'bd3',
    vehicleId: 'v2',
    date: '2026-02-10',
    location: 'TotalEnergies Manyara junction',
    odometer: 156400,
    issue: 'Alternator failure — battery not charging',
    status: 'under-repair',
    reportedBy: 'Sarah',
    attachmentPhoto: null,
    attachmentPhotoName: ''
  },
  {
    id: 'bd4',
    vehicleId: 'v3',
    date: '2026-02-18',
    location: 'Babati town centre',
    odometer: 148900,
    issue: 'Starter motor fault — vehicle will not crank',
    status: 'reported',
    reportedBy: 'Michael',
    attachmentPhoto: null,
    attachmentPhotoName: ''
  }
];

export function getBreakdownRecordsForVehicle(vehicleId, records = BREAKDOWN_RECORDS) {
  return records
    .filter((r) => r.vehicleId === vehicleId)
    .sort((a, b) => b.date.localeCompare(a.date));
}

export function getBreakdownStatusLabel(status) {
  const map = {
    reported: 'Reported',
    'under-repair': 'Under repair',
    resolved: 'Resolved'
  };
  return map[status] || status;
}

export function getBreakdownStatusClass(status) {
  const map = {
    reported: 'pending',
    'under-repair': 'in-progress',
    resolved: 'completed'
  };
  return map[status] || 'pending';
}

export const VIR_CHECKLIST_CATEGORIES = [
  {
    id: 'underhood',
    label: 'UNDERHOOD',
    items: [
      'Oil level',
      'Radiator level',
      'Battery level',
      'Windshield washer fluid level',
      'Engine hoses & belts'
    ]
  },
  {
    id: 'exterior',
    label: 'EXTERIOR',
    items: [
      'Tires (Inflation, tread depth)',
      'Turning signals & brake lights',
      'Head lights (high and low beam)',
      'Windshield wiper',
      'Body',
      'Cleanliness',
      'Availability of spare wheel'
    ]
  },
  {
    id: 'interior',
    label: 'INTERIOR',
    items: [
      'Springs',
      'Shocks',
      'Exhaust system',
      'Steering',
      'Horn & mirror',
      'Brakes working'
    ]
  },
  {
    id: 'vehicle-interior-equipment',
    label: 'VEHICLE INTERIOR EQUIPMENT',
    items: [
      'Equipped first aid kit',
      'Serviced fire extinguisher',
      'Reflective triangle',
      'Spare tire',
      'Jack and handle'
    ]
  },
  {
    id: 'documents',
    label: 'DOCUMENTS',
    items: [
      'Vehicle registration document',
      'Valid vehicle insurance'
    ]
  },
  {
    id: 'rear-wheel-pickup',
    label: 'BACK OF A REAR-WHEEL PICKUP',
    items: []
  }
];

export function getVirChecklistItems(categories = VIR_CHECKLIST_CATEGORIES) {
  return categories.flatMap((category) => category.items);
}

export const VIR_CHECKLIST_ITEMS = getVirChecklistItems();

export function createDefaultVirChecks(value = '') {
  return Object.fromEntries(VIR_CHECKLIST_ITEMS.map((item) => [item, value]));
}

export function isVirChecklistComplete(checks) {
  return VIR_CHECKLIST_ITEMS.every((item) => ['pass', 'fail', 'na'].includes(checks[item]));
}

export function getVirCheckOptionLabel(option) {
  if (option === 'pass') return 'OK';
  if (option === 'fail') return 'NOT OKAY';
  return 'NA';
}

export function getVirCheckOptionsForCategory(categoryId) {
  if (categoryId === 'documents') return ['pass', 'fail'];
  return ['pass', 'fail', 'na'];
}

export const VIR_TICKET_ITEMS = [
  { id: 'trafficOffense', label: 'Traffic offense ticket' },
  { id: 'parkingFee', label: 'Parking fee ticket' }
];

export function createDefaultVirTickets() {
  return {
    trafficOffense: { hasTicket: 'no', amount: '' },
    parkingFee: { hasTicket: 'no', amount: '' }
  };
}

/** @param {{ vehicleId: string; date: string; virNo?: string; plate?: string }} inspection */
export function resolveVirNumber(inspection) {
  if (inspection.virNo) return inspection.virNo;
  const vehicle = FLEET_VEHICLES.find((v) => v.id === inspection.vehicleId);
  const plate = String(inspection.plate || vehicle?.plate || inspection.vehicleId || 'VEH')
    .replace(/\s/g, '')
    .toUpperCase();
  const datePart = String(inspection.date || '').replace(/-/g, '');
  return `VIR-${plate}-${datePart}`;
}

/** @type {Array<{ id: string; vehicleId: string; date: string; virNo?: string; odometer: number | null; inspector: string; overall: 'pass' | 'fail' | 'inspect'; inspectedDate?: string | null; notes: string; checks: Record<string, 'pass' | 'fail' | 'na'>; scheduled?: boolean }>} */
export const VIR_INSPECTIONS = [
  {
    id: 'vir2',
    vehicleId: 'v2',
    date: '2026-02-15',
    virNo: 'VIR-T112ESF-20260215',
    odometer: 157900,
    inspector: 'Philipo',
    overall: 'fail',
    notes: 'Rear indicator not working — repair scheduled.',
    checks: Object.fromEntries(VIR_CHECKLIST_ITEMS.map((item) => [item, item.includes('brake lights') ? 'fail' : 'pass']))
  },
  {
    id: 'vir3',
    vehicleId: 'v3',
    date: '2026-01-20',
    virNo: 'VIR-T993ESE-20260120',
    odometer: 148200,
    inspector: 'KIPRUTO Shadrack',
    overall: 'pass',
    notes: 'Minor wiper wear noted.',
    checks: Object.fromEntries(VIR_CHECKLIST_ITEMS.map((item) => [item, 'pass']))
  },
  {
    id: 'vir4',
    vehicleId: 'v4',
    date: '2026-02-10',
    virNo: 'VIR-T104ESF-20260210',
    odometer: 139200,
    inspector: 'Admin',
    overall: 'pass',
    notes: 'Vehicle in good condition.',
    checks: Object.fromEntries(VIR_CHECKLIST_ITEMS.map((item) => [item, 'pass']))
  }
];

export function enrichFuelRecordsForVehicle(vehicleId, records = FUEL_RECORDS) {
  const vehicleRecords = records
    .filter((r) => r.vehicleId === vehicleId)
    .sort((a, b) => a.date.localeCompare(b.date));

  const enriched = vehicleRecords.map((record, index) => {
    const prior = vehicleRecords[index - 1];
    const previousOdometer = record.previousOdometer ?? prior?.odometer ?? record.odometer;
    return { ...record, previousOdometer };
  });

  return enriched.sort((a, b) => b.date.localeCompare(a.date));
}

export function getFuelRecordsForVehicle(vehicleId, records = FUEL_RECORDS) {
  return enrichFuelRecordsForVehicle(vehicleId, records);
}

/** Weekly VIR rows: every Friday from 1 July 2026 through today (inclusive). */
const VIR_SCHEDULE_START = { year: 2026, month: 7, day: 1 };
/** Dates already marked completed without a full checklist submission. */
const PRE_INSPECTED_VIR_DATES = new Set([]);

function formatIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Fridays from a start date (inclusive) through untilDate (inclusive).
 * Default start: 1 July 2026. First Friday on/after that date is included.
 */
export function getFridaysFromMonth(year, month, untilDate = new Date(), day = 1) {
  const fridays = [];
  const cursor = new Date(year, month - 1, day);
  while (cursor.getDay() !== 5) {
    cursor.setDate(cursor.getDate() + 1);
  }
  const end = new Date(untilDate);
  end.setHours(23, 59, 59, 999);
  while (cursor <= end) {
    fridays.push(formatIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 7);
  }
  return fridays;
}

export function generateScheduledVirInspections(vehicleId, inspector, records = VIR_INSPECTIONS, vehicleContext = null) {
  const existingDates = new Set(
    records.filter((r) => r.vehicleId === vehicleId).map((r) => r.date)
  );
  const fridays = getFridaysFromMonth(
    VIR_SCHEDULE_START.year,
    VIR_SCHEDULE_START.month,
    new Date(),
    VIR_SCHEDULE_START.day
  );
  const fleetVehicle = FLEET_VEHICLES.find((v) => v.id === vehicleId);
  const plate = vehicleContext?.plate || fleetVehicle?.plate || '';
  const odometerFallback = vehicleContext?.odometer ?? fleetVehicle?.odometer ?? null;

  return fridays
    .filter((date) => !existingDates.has(date))
    .map((date) => {
      const isPreInspected = PRE_INSPECTED_VIR_DATES.has(date);
      const row = {
        id: `vir-sched-${vehicleId}-${date}`,
        vehicleId,
        plate,
        date,
        odometer: isPreInspected ? odometerFallback : null,
        inspector,
        overall: isPreInspected ? 'pass' : 'inspect',
        inspectedDate: isPreInspected ? date : null,
        notes: isPreInspected ? 'Inspection completed.' : 'Weekly inspection due',
        checks: {},
        scheduled: !isPreInspected
      };
      return { ...row, virNo: resolveVirNumber(row) };
    });
}

/**
 * Completed + weekly Friday schedule for a vehicle.
 * `vehicleContext` lets rental-only vehicles (no fleet mock id) still get Friday rows.
 */
export function getVirForVehicle(vehicleId, records = VIR_INSPECTIONS, vehicleContext = null) {
  const fleetVehicle = FLEET_VEHICLES.find((v) => v.id === vehicleId);
  const inspector =
    vehicleContext?.driver || vehicleContext?.inspector || fleetVehicle?.driver || 'Driver';
  const plate = vehicleContext?.plate || fleetVehicle?.plate || '';
  const completed = records
    .filter((r) => r.vehicleId === vehicleId)
    .map((r) => ({ ...r, plate: r.plate || plate, virNo: resolveVirNumber({ ...r, plate: r.plate || plate }) }));
  const scheduled = generateScheduledVirInspections(vehicleId, inspector, records, {
    plate,
    odometer: vehicleContext?.odometer ?? fleetVehicle?.odometer ?? null
  });
  return [...completed, ...scheduled].sort((a, b) => b.date.localeCompare(a.date));
}

export function getVirResultLabel(overall) {
  if (overall === 'inspect') return 'Inspect Vehicle';
  return 'Vehicle Inspected';
}

export function getVirResultStatusClass(overall) {
  if (overall === 'inspect') return 'vir-inspect';
  return 'vir-inspected';
}

export function getDrivingLicenseForVehicle(vehicleId, records = DRIVING_LICENSES) {
  return records.find((r) => r.vehicleId === vehicleId) || null;
}

export function getLicenseStatus(expiryDate) {
  const today = new Date();
  const expiry = new Date(expiryDate);
  const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (days < 0) return { key: 'expired', label: 'Expired', days };
  if (days <= 60) return { key: 'expiring', label: 'Expiring soon', days };
  return { key: 'valid', label: 'Valid', days };
}
